import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import PropTypes from "prop-types";

import { teal } from "../../theme/colors";

/**
 * CardPaper
 *
 * Shared paper visual for list-style cards (OrderCard, PropertyCard, future
 * collection cards). Just the wrapper — background / border / radius / soft
 * shadow / hover deepening. Layout inside is the consumer's call.
 *
 * Pass `selected` to apply a primary-accent border + soft glow ring so cards
 * that act as selectable list items can show their active state without
 * reimplementing the whole hover/selection palette.
 */
const ACCENT = teal[500];

const CardPaper = ({
  selected = false,
  selectedColor = ACCENT,
  onClick,
  sx,
  component = "div",
  children,
  ...rest
}) => {
  const interactive = typeof onClick === "function";
  return (
    <Box
      component={component}
      onClick={onClick}
      sx={{
        background: "#fff",
        border: `1px solid ${selected ? selectedColor : grey[200]}`,
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: selected
          ? `0 0 0 3px ${selectedColor}1f, 0 1px 2px rgba(16,24,40,0.04)`
          : "0 1px 2px rgba(16,24,40,0.04)",
        cursor: interactive ? "pointer" : "default",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover": interactive
          ? {
              borderColor: selected ? selectedColor : grey[300],
              boxShadow: selected
                ? `0 0 0 3px ${selectedColor}33, 0 4px 12px rgba(16,24,40,0.06)`
                : "0 4px 12px rgba(16,24,40,0.06)",
            }
          : {},
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

CardPaper.propTypes = {
  selected: PropTypes.bool,
  selectedColor: PropTypes.string,
  onClick: PropTypes.func,
  sx: PropTypes.object,
  component: PropTypes.elementType,
  children: PropTypes.node,
};

export default CardPaper;
